import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_IDENTITY_API ?? '/api/v1';

export type UserStatus = 'active' | 'inactive' | 'suspended' | 'archived';

export type User = {
  id: string;
  email: string;
  username?: string;
  display_name: string;
  given_name?: string;
  family_name?: string;
  bio?: string;
  avatar_url?: string;
  status: UserStatus;
  contact?: { email?: string; phone?: string; alternate_email?: string };
  professional?: { job_title?: string; department?: string; company?: string; location?: string };
  metadata?: Record<string, string>;
  tags?: string[];
  created_at: string;
  updated_at: string;
  last_active_at?: string;
};

export type UserPreferences = {
  user_id: string;
  language: string;
  timezone: string;
  locale: string;
  theme: string;
  default_organization_id?: string;
  default_tenant_id?: string;
  default_workspace_id?: string;
  notifications?: Record<string, boolean>;
  accessibility?: Record<string, string>;
  updated_at: string;
};

export type ActivityEntry = { id: string; action: string; summary?: string; created_at: string };
export type AuditEntry = { id: string; action: string; outcome: string; created_at: string };

function headers(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...getScopeHeaders(),
    ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
  };
}

async function idFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers: { ...headers(), ...(init?.headers as Record<string, string> | undefined) } });
  if (!res.ok) throw new Error(`Users ${path} failed: ${res.status} ${await res.text()}`);
  const body = await res.json();
  return (body.data ?? body) as T;
}

export const userApi = {
  list: (params?: { status?: string; q?: string; tag?: string }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.q) qs.set('q', params.q);
    if (params?.tag) qs.set('tag', params.tag);
    const query = qs.toString();
    return idFetch<User[]>(`/users${query ? `?${query}` : ''}`);
  },
  search: (params?: Record<string, string>) =>
    idFetch<{ results: User[]; count: number }>(`/users/search?${new URLSearchParams(params).toString()}`),
  get: (id: string) => idFetch<User>(`/users/${id}`),
  create: (input: Partial<User> & { email: string; display_name: string }) =>
    idFetch<User>('/users', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, input: Partial<User>) =>
    idFetch<User>(`/users/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
  patch: (id: string, input: Partial<User>) =>
    idFetch<User>(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  delete: (id: string) => idFetch<void>(`/users/${id}`, { method: 'DELETE' }),
  getPreferences: (id: string) => idFetch<UserPreferences>(`/users/${id}/preferences`),
  updatePreferences: (id: string, input: Partial<UserPreferences>) =>
    idFetch<UserPreferences>(`/users/${id}/preferences`, { method: 'PUT', body: JSON.stringify(input) }),
  getActivity: (id: string) => idFetch<ActivityEntry[]>(`/users/${id}/activity`),
  getAudit: (id: string) => idFetch<AuditEntry[]>(`/users/${id}/audit`),
};
